'use client';

import { CheckCircle2, PhoneOff, RefreshCw, WifiOff } from 'lucide-react';
import { useConnectionStatus, type ConnectionStatus } from '@/lib/connection-status';
import { Banner, BANNER_ORDER } from './banner-stack/BannerStack';

const COPY: Record<NonNullable<ConnectionStatus>, string> = {
  offline: "You're offline. Reconnecting…",
  call: 'Reconnecting to the call…',
  ended: 'This call has ended',
  restored: 'Back online',
};
const ICON = { offline: WifiOff, call: RefreshCw, ended: PhoneOff, restored: CheckCircle2 } as const;
export function ConnectionStatusBanner() {
  const status = useConnectionStatus();
  if (!status) return null;
  const Icon = ICON[status];
  return (
    <Banner role="status" order={BANNER_ORDER.connection} testId="connection-status-banner"
      attrs={{ 'aria-live': status === 'offline' ? 'assertive' : 'polite', 'aria-atomic': 'true' }}
      className="connection-status-banner bg-surface text-ink-secondary">
      <div>
        <div className="connection-status-content flex items-center justify-center gap-3 border-b border-edge px-4 py-2 text-sm">
          <Icon aria-hidden="true" className={`h-4 w-4 shrink-0 ${status === 'restored' ? 'text-success' : 'text-ink-muted'}`} />
          <span>{COPY[status]}</span>
        </div>
      </div>
    </Banner>
  );
}

/** The same state in the call header, without another announcement or timer. */
export function CallConnectionIndicator() {
  const status = useConnectionStatus();
  if (!status) return null;
  const Icon = ICON[status];
  return <span data-testid="call-connection-indicator" className="inline-flex items-center gap-1.5 text-xs text-ink-secondary">
    <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-ink-muted" />
    <span>{COPY[status]}</span>
  </span>;
}
